/**
 * The programme sheet: a proposed list of moves, and the one press that approves
 * it as a batch.
 *
 * The list came from one paid pass (`planProgramme`) and has landed nothing. This
 * surface prints every step with the model's sentence beside it, the budget the
 * list implies and an estimate of what it would draw, and offers exactly two
 * presses that decide it: discard, or approve what is still ticked and run. (A
 * third, "Add the key", appears only beside the sentence saying what blocks the
 * approval, and decides nothing.) Approval hands the list to `startRun`, where
 * the loop walks it in this order and chooses nothing (invariant 6 holds over
 * the run; invariant 9 is the press). Unticking a step
 * before approving is still one approval of one batch — a shorter one.
 *
 * Nothing here is remembered across a reload — the draft lives in the store on
 * the `lastScan` rule — so a list proposed against a graph that has since
 * changed cannot come back and spend against the new one.
 */

import { useEffect, useMemo, useState } from 'react'
import { OP_LABEL, type ProgrammeStep, type RunBudget } from '../core/types'
import { fmtUsd, type Role } from '../core/models'
import { entryFor, programmeClaimKey, resolveFor, useStore } from '../core/store'
import { DEFAULT_EVIDENCE_PROFILE, normalizeEvidenceProfile } from '../core/epistemic'
import {
  budgetFor,
  budgetForEvidenceProfile,
  evidenceProfileCeiling,
  nextProgrammeStep,
  programmeAuditors,
  programmeFits,
  programmeScanInPlay,
  programmeSizing,
  closingScanUsd,
  projectVerbUsd,
  reasonWillRun,
  startRun,
} from '../engine/run'

/** Whether this budget permits a step of that kind at all. The same three
 *  allowances `programmeAllows` reads inside the loop, asked before the press so
 *  the sheet never promises a pass the run will skip. */
const allowsStep = (
  budget: Pick<RunBudget, 'checks' | 'panels' | 'audits'>,
  verb: ProgrammeStep['verb'],
): boolean =>
  verb === 'verify'
    ? budget.checks > 0
    : verb === 'panel'
      ? (budget.panels ?? 0) > 0
      : verb === 'audit'
        ? (budget.audits ?? 0) > 0
        : true

/**
 * Why the approval is off, and which key would turn it on.
 *
 * `key` is the route the sheet's "Add the key" press opens the settings sheet
 * on: a role's own key, `'other'` for the cross-examination's second company,
 * or null where no key would help — a budget the protocol caps, a graph that
 * has moved. Kept beside the sentence rather than parsed back out of it, so the
 * words can change without the press losing its way.
 */
type Missing = { why: string; key: Role | 'other' | null }

export function ProgrammeSheet() {
  const held = useStore((s) => s.programmeDraft)
  const setDraft = useStore((s) => s.setProgrammeDraft)
  const session = useStore((s) => s.session)
  /**
   * The list is a proposal about the graph it was proposed against, so a graph
   * that has moved since retires it rather than spending against the new one —
   * the `lastScan` rule, at the price of a paid pass. Held rather than cleared
   * on sight, so the sheet can say what happened instead of the list silently
   * vanishing between a person reading it and reaching for the button.
   */
  const stale =
    !!held &&
    (held.sessionId !== session.id ||
      held.claims !== programmeClaimKey(session.nodes, held.programme))
  const draft = held?.programme ?? null
  const nodes = session.nodes
  const runCeiling = useStore((s) => s.runCeiling)
  const evidenceProfile = useStore((s) => s.session.research?.evidenceProfile)
  const busy = useStore((s) => s.busy)
  const live = useStore((s) => s.run)
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const keys = useStore((s) => s.keys)
  // The reading too: the closing scan is priced from the prompt it would send,
  // and the prompt is built under the lens and temper this workspace holds.
  const lens = useStore((s) => s.lens)
  const temper = useStore((s) => s.temper)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  // One selector per answer, each a boolean or a name. zustand compares a
  // selector's answers by identity, so one that builds an object never matches
  // its last and the sheet rendered until React gave up, blanking the page
  // (D328). `resolveFor` builds one per call once a key is set, so even a
  // shallow compare of an object of them looped for everybody with a key.
  const reasonReady = useStore((s) => resolveFor(s, 'reason') !== null)
  const checkReady = useStore((s) => resolveFor(s, 'check') !== null)
  const panelReady = useStore((s) => resolveFor(s, 'panel') !== null)
  // The CLOSING SCAN's own role, which is not the check's. Every run ends on
  // one where its budget can reach one, and `startRun` refuses without this
  // route — so leaving it out enabled an approval that met that refusal.
  const scanReady = useStore((s) => resolveFor(s, 'scan') !== null)
  const reasonName = useStore((s) => entryFor(s, 'reason').name)
  const checkName = useStore((s) => entryFor(s, 'check').name)
  const scanName = useStore((s) => entryFor(s, 'scan').name)
  const [off, setOff] = useState<Set<number>>(() => new Set())
  // A new list is a new set of ticks: an index unticked on the last proposal
  // means nothing about a step that happens to sit at the same index now.
  useEffect(() => setOff(new Set()), [draft])

  const kept = useMemo(
    () =>
      draft ? draft.steps.filter((_, i) => !off.has(i) && !!nodes[draft.steps[i]!.nodeId]) : [],
    [draft, off, nodes],
  )
  const sizing = useMemo(
    () => (draft ? programmeSizing({ ...draft, steps: kept }) : null),
    [draft, kept],
  )
  /**
   * What this programme would draw, estimated at list prices: the ticked steps
   * plus the closing scan, which every run ends on and which the sentence under
   * this list already promises. The scan was missing, so the figure sat below
   * what the sheet's own words said the run would do — the one direction an
   * estimate in this app is never allowed to be wrong (invariant 5).
   *
   * Null when any pass in the sum has no published rate: a partial total printed
   * as the whole is a smaller number than the truth, which is the same failure
   * from the other side. No figure beats a wrong one.
   */
  /**
   * The model each audit step would run on, by target.
   *
   * An audit resolves against the TARGET BRANCH's author rather than against the
   * selection, so one list's audits can reach several houses. `projectVerbUsd`
   * requires that entry for an audit and returns null without it — so asking it
   * for an audit and passing nothing suppressed the WHOLE estimate on every
   * programme containing one, including the ordinary priced case.
   */
  // One view, built once. `programmeAuditors` and `programmeScanInPlay` both
  // resolve an audit's house from it, and two spellings of the same object is
  // two answers waiting to differ about which model a step runs on.
  const view = useMemo(
    () => ({ modelId, keys, customBase, customModel, roleModels, session, lens, temper }),
    [modelId, keys, customBase, customModel, roleModels, session, lens, temper],
  )
  const auditors = useMemo(() => {
    const by = new Map<string, ReturnType<typeof programmeAuditors>[number]['auditor']>()
    for (const a of programmeAuditors(view, kept)) {
      by.set(a.nodeId, a.auditor)
    }
    return by
  }, [kept, view])

  const estimate = useMemo(() => {
    let usd = 0
    // The closing scan, when this run will make one — the budget's allowance AND
    // a list that produces nodes for the scan to have something to join. Pricing
    // it off the budget alone charged a check-only programme for a pass that
    // cannot fire, and the refusal two guards below asked the same question the
    // same wrong way. `programmeScanInPlay` is the one answer.
    if (sizing && programmeScanInPlay(budgetFor(sizing), kept, view)) {
      // Priced from the prompt the scan would actually send, never as a move:
      // a move's figure is low on a large graph, the one direction invariant 5
      // forbids.
      const scan = closingScanUsd(view)
      if (scan === null) return null
      usd += scan
    }
    for (const st of kept) {
      const verb =
        st.verb === 'verify'
          ? 'verify'
          : st.verb === 'panel'
            ? 'panel'
            : st.verb === 'audit'
              ? 'audit'
              : 'move'
      // The auditor rides along for an audit and is ignored for everything else.
      // A step whose auditor will not resolve leaves no figure rather than a
      // total missing a pass — `startRun` refuses that programme outright, and a
      // sum that quietly dropped the step would be lower than the truth, which
      // is the one direction this figure may not err (invariant 5).
      const one = projectVerbUsd(
        { modelId, roleModels },
        verb,
        verb === 'audit' ? (auditors.get(st.nodeId)?.entry ?? null) : null,
      )
      if (one === null) return null
      usd += one
    }
    return usd
  }, [kept, sizing, modelId, roleModels, auditors, view])

  /**
   * The routes THIS list will actually take.
   *
   * `useCanCall` asks whether the SELECTED model is callable, which is a
   * question no step on this list necessarily asks: a check goes to the `check`
   * role, an audit to a house other than its target's, and a list of nothing but
   * checks makes no reasoning pass at all. So a callable checker under an
   * unkeyed selection left a valid check-only programme permanently disabled —
   * and `startRun`'s own `reasonWillRun` guard, added for exactly that list,
   * could never be reached to do its job.
   *
   * Each role is asked about only where the list reaches it, which is the rule
   * `rolesInPlay` keeps in `startRun`: a programme that will make no panel is
   * not refused for a panel key.
   */
  const missing = ((): Missing | null => {
    if (!draft || !sizing || !kept.length) return null
    if (stale) return { why: 'a graph they were proposed against', key: null }
    // The budget this list will actually have. A durable evidence profile can
    // take the check or panel allowance to zero, and `nextProgrammeStep` then
    // SKIPS those steps — so requiring their keys because the raw list mentions
    // them disables a programme whose every executable pass is routed and keyed.
    // The same construction `startRun` performs, asked here so the sheet and the
    // run agree rather than the button being a stricter guess at it.
    const profile = normalizeEvidenceProfile(evidenceProfile ?? DEFAULT_EVIDENCE_PROFILE)
    const spec = { ...sizing, usdMax: runCeiling ? Number(runCeiling) : null }
    const budget = budgetForEvidenceProfile(
      spec,
      profile,
      evidenceProfileCeiling(spec.usdMax, profile),
    )
    if (reasonWillRun({ steps: kept, model: draft.model, proposedAt: draft.proposedAt }))
      if (!reasonReady) return { why: `your ${reasonName} key`, key: 'reason' }
    if (budget.checks > 0 && kept.some((st) => st.verb === 'verify') && !checkReady)
      return { why: `your ${checkName} key`, key: 'check' }
    if ((budget.panels ?? 0) > 0 && kept.some((st) => st.verb === 'panel') && !panelReady)
      return { why: 'a key for the panel role', key: 'panel' }
    // An audit resolves against its own target's branch, so the sheet asks the
    // same per-target question the estimate above does rather than a role.
    if (
      (budget.audits ?? 0) > 0 &&
      kept.some((st) => st.verb === 'audit' && !auditors.get(st.nodeId))
    )
      return { why: 'a key for a second provider, which a cross-examination runs on', key: 'other' }
    // The scan's key exactly where the run will make a scan — which is a
    // question about the LIST as well as the budget. A list of checks produces
    // no nodes, so `scanWillRun`'s floor is unreachable however tall the budget
    // is, and asking the budget alone refused a whole run over a key nothing
    // would have used. `programmeScanInPlay` is the engine's own answer.
    if (programmeScanInPlay(budget, kept, view) && !scanReady)
      return { why: `your ${scanName} key for the closing scan`, key: 'scan' }
    /**
     * And a list the budget allows NO step of is not runnable at all.
     *
     * A profile can cap every retained step out — one `verify` under
     * `maxMoves: 1` leaves `budget.checks` at zero — and the sheet was then
     * enabled because no ROUTE was missing. `startRun` fires `onStarted`, which
     * discards the paid list, and `nextProgrammeStep` skips the only step and
     * ends the run having taken no pass. The third shape of "started, ate the
     * list, did nothing", and the one the sheet can see coming.
     *
     * Asked through `nextProgrammeStep` rather than by counting verbs here,
     * because it is the engine's own answer to "which step is next" — it also
     * skips a pruned target and a `verify` on the seed, and a second reading of
     * that would be a second thing to keep in step.
     */
    // A list the budget allows only PART of is the same promise broken by half:
    // the sheet prints these steps and the button says "approve N steps and
    // run", so a run that takes fewer — having discarded the list in
    // `onStarted` — did not do what was approved. Refused rather than silently
    // filtered, because filtering changes what somebody is approving after they
    // have read it, and the choice is theirs: untick the capped steps, or raise
    // the protocol's ceiling.
    // Every KIND permitted, and the WHOLE LIST within the count. `allowsStep`
    // answers the first and is silent on the second — five `deepen` steps under
    // `maxMoves: 2` each pass it and only two of them run — so `programmeFits`
    // asks the cumulative question, in the engine, off `programmeSizing`'s own
    // tally rather than a second copy of it here.
    if (kept.some((st) => !allowsStep(budget, st.verb)) || !programmeFits(budget, kept, view)) {
      return {
        why: 'a budget that allows all of them — this evidence protocol permits only some',
        key: null,
      }
    }
    if (
      nextProgrammeStep({ ...draft, steps: kept }, 0, nodes, (verb) => allowsStep(budget, verb)) ===
      null
    ) {
      return {
        why: 'a budget that allows at least one of them — this evidence protocol permits none',
        key: null,
      }
    }
    return null
  })()

  if (!draft || !sizing) return null
  const toggle = (i: number) =>
    setOff((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  const approve = () => {
    if (!kept.length) return
    const programme = { ...draft, steps: kept }
    // The draft is cleared when the run STARTS, not when the button is pressed.
    // This press only knows the selected model resolves; `startRun` refuses for
    // several more reasons — a check or panel route with no key, an auditor that
    // will not resolve, a closing-scan route that will not, an unpriced model
    // under a ceiling — and every one of them is a settings problem the person
    // can fix in a minute. Clearing here threw away a list they had paid a pass
    // for and made them pay again.
    void startRun(
      {
        ...sizing,
        programme,
        usdMax: runCeiling ? Number(runCeiling) : null,
      },
      { onStarted: () => setDraft(null) },
    )
  }
  /**
   * Put the list away, with the way back on the toast (D335).
   *
   * The list was a paid pass, and Discard sits a thumb's width from Approve, so
   * one mis-tap threw away what the reader had bought — every other removal in
   * the app offers "Put it back". The WHOLE held draft goes back, its `claims`
   * key included, rather than through `setProgrammeDraft`, which restamps the
   * key against the graph as it stands now and so would hide the stale warning
   * on a list proposed against an older one.
   *
   * Refused, and said, where it would overwrite something: a newer list on the
   * sheet (also paid for), or another exploration open — the list names this
   * one's claims. The toast stays up through a refusal, so the offer survives
   * a trip back to the exploration it belongs to.
   */
  const discard = () => {
    const was = useStore.getState().programmeDraft
    setDraft(null)
    if (!was) return
    useStore.getState().toast('Programme put away. Nothing ran.', 'info', {
      label: 'Put it back',
      run: () => {
        const s = useStore.getState()
        if (s.programmeDraft) {
          s.toast(
            'A newer programme is on the sheet. Discard it first to bring this one back.',
            'warn',
          )
          return false
        }
        if (s.session.id !== was.sessionId) {
          s.toast(
            'That programme was proposed on another exploration. Open it to bring it back.',
            'warn',
          )
          return false
        }
        useStore.setState({ programmeDraft: was })
      },
    })
  }
  // The same card the run's own refusal opens (`askForMissingKey`), so the note
  // here and the sentence on that card are one route. The cross-examination's
  // key has no role to name — any second company will do — so it opens on the
  // providers rows.
  const addKey = (key: Role | 'other') => {
    const s = useStore.getState()
    if (key === 'other') s.setSetup('settings', 'providers')
    else s.askForMissingKey(key)
  }
  const counts = {
    moves: kept.filter((s) => s.verb !== 'verify' && s.verb !== 'panel' && s.verb !== 'audit')
      .length,
    checks: kept.filter((s) => s.verb === 'verify').length,
    panels: kept.filter((s) => s.verb === 'panel').length,
    audits: kept.filter((s) => s.verb === 'audit').length,
  }

  return (
    <div className="programme panel" role="dialog" aria-label="A programme to approve">
      <span className="eyebrow">
        Programme · {kept.length} of {draft.steps.length} steps · proposed by {draft.model.name}
      </span>
      <p className="gnote">
        Each step names a claim, a verb and the model’s one sentence on why. Untick what you do not
        want; the rest run in this order as one batch, and the run chooses nothing itself. It stops
        at the end of the list, or on any stop every run shares.
      </p>
      <ol className="programme-steps">
        {draft.steps.map((st, i) => {
          const n = nodes[st.nodeId]
          return (
            <li key={i} data-off={off.has(i) || !n || undefined}>
              <label>
                <input
                  type="checkbox"
                  checked={!off.has(i) && !!n}
                  disabled={!n}
                  onChange={() => toggle(i)}
                />
                <span className="programme-step">
                  <b>
                    {OP_LABEL[st.verb]} · {n ? n.label : 'a claim since pruned'}
                  </b>
                  <span>{st.why}</span>
                </span>
              </label>
            </li>
          )
        })}
      </ol>
      <p className="gnote">
        {counts.moves} reasoning pass{counts.moves === 1 ? '' : 'es'}, {counts.checks} source check
        {counts.checks === 1 ? '' : 's'}, {counts.panels} panel{counts.panels === 1 ? '' : 's'},{' '}
        {counts.audits} cross-examination{counts.audits === 1 ? '' : 's'}, then the closing scan
        every run ends on — on the {sizing.moves}-move row
        {estimate !== null ? `, about ${fmtUsd(estimate)} estimated, leaning high` : ''}
        {runCeiling ? `, under the ${fmtUsd(Number(runCeiling))} ceiling set in the composer` : ''}.
      </p>
      {/* Said out loud rather than left to a tooltip on a disabled button: the
          list still reads as approvable, and the reason it is not is a fact
          about the graph rather than about the list. */}
      {stale && (
        <p className="gnote gnote--warn">
          The graph has changed since this list was proposed, so its steps name claims that may no
          longer be the ones it meant. Nothing here can run — discard it and plan again.
        </p>
      )}
      {/* The other reasons, said out loud on the same rule (D335). A disabled
          button raises no tooltip on touch and seldom one for a screen reader,
          so a reason that lived only in its title left a list somebody paid
          for beside a dead press. Where the fix is a key, the press that opens
          its card sits with the sentence; the list stays held through the
          detour, which is why `approve` clears it only once a run starts. */}
      {missing && !stale && (
        <p className="gnote gnote--warn">
          These steps need {missing.why}.{' '}
          {missing.key && (
            <button className="linkish" onClick={() => addKey(missing.key!)}>
              Add the key
            </button>
          )}
        </p>
      )}
      <div className="programme-foot">
        <button className="btn" onClick={discard} title="Put the list away. Nothing ran.">
          Discard
        </button>
        {/* The sheet's one primary, as every other approve-and-spend press in
            the app is (D335); Discard stays plain beside it. */}
        <button
          className="btn btn--primary"
          disabled={busy || !!live || missing !== null || !kept.length}
          onClick={approve}
          title={
            stale
              ? 'The graph has changed since this list was proposed, so its steps name claims that may no longer be the ones it meant. Discard it and plan again.'
              : missing
                ? `These steps need ${missing.why}.`
                : live
                  ? 'A run is already going.'
                  : 'Run these steps, in this order, as one batch.'
          }
        >
          Approve {kept.length} step{kept.length === 1 ? '' : 's'} and run
        </button>
      </div>
    </div>
  )
}
